// Data layer langganan Web Push (tabel push_subscriptions, mig 150). Mengelola
// langganan PushManager browser + baris DB ber-scope (customer/admin). Satu SW
// (scope "/") → satu langganan browser; baris DB-lah yang menentukan apakah suatu
// scope menerima push (hapus baris = berhenti, tanpa melepas langganan browser
// agar tak mengganggu scope/role lain di perangkat yang sama).

import { createBrowserSupabase } from "@/lib/supabase/browser";
import {
  isPushSupported,
  mutedTypesFromPrefs,
  normalizeVapidPublicKey,
  sameApplicationServerKey,
  urlBase64ToUint8Array,
  VAPID_PUBLIC_KEY,
} from "@/lib/push";
import { SITE_SETTING_KEYS } from "@/lib/siteSettings";
import { readNotifPrefs } from "@/lib/notificationPrefs";

export type PushScope = "customer" | "admin";

/** Jenis yang di-mute untuk scope ini (server-side opt-out, mig 152). Hanya
 *  pembeli yang memakai mute per-jenis; notif admin sudah disaring saat dibuat. */
function mutedTypesForScope(scope: PushScope): string[] {
  return scope === "customer" ? mutedTypesFromPrefs(readNotifPrefs()) : [];
}

type Result = { ok: boolean; error?: string };

function clientFor(scope: PushScope) {
  return createBrowserSupabase(scope === "admin" ? "admin" : "store");
}

async function readyRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

/** Apakah scope ini aktif: izin granted + ada langganan browser + ada baris DB. */
export async function isPushEnabled(scope: PushScope): Promise<boolean> {
  if (!isPushSupported() || Notification.permission !== "granted") return false;
  const reg = await readyRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) return false;
  const supabase = clientFor(scope);
  if (!supabase) return false;
  const { data } = await supabase
    .from("push_subscriptions")
    .select("id")
    .eq("scope", scope)
    .eq("endpoint", sub.endpoint)
    .maybeSingle();
  return Boolean(data);
}

/**
 * Kunci publik VAPID efektif, URUT prioritas (【6】T6, mig 3280):
 *   1. `store_push_keys` TOKO ini — lewat RPC anon `store_vapid_public_key`
 *      (kunci PRIVAT tidak pernah ikut; tabelnya platform-only tanpa policy
 *      baca). Ini yang membuat push benar-benar per-toko.
 *   2. `site_settings.vapid_public_key` TOKO ini (jalur runtime lama, mig 196).
 *   3. env build `NEXT_PUBLIC_VAPID_PUBLIC_KEY` (di-inline saat build; hilang
 *      bila build baru tak membawa env-nya).
 *
 * GALEA EXISTING TIDAK PUTUS: mig 3280 menyalin kunci publik dari
 * `site_settings` ke `store_push_keys`, jadi nilai di langkah 1 IDENTIK dengan
 * yang sudah dipakai browser → `applicationServerKey` tidak berubah →
 * langganan lama tetap sah (tidak ada unsubscribe paksa).
 *
 * `.eq("key", …).maybeSingle()` lama juga diperbaiki di sini: pasca PK
 * `(store_id, key)` (mig 3160) query tanpa filter toko bisa mengembalikan >1
 * baris dan MELEMPAR — kini di-filter store_id eksplisit.
 */
async function resolveVapidPublicKey(scope: PushScope): Promise<string> {
  const supabase = clientFor(scope);
  if (supabase) {
    const storeId = await resolveClientStoreId(supabase);
    if (storeId) {
      try {
        const { data } = await supabase.rpc("store_vapid_public_key", {
          p_store: storeId,
        });
        const key = normalizeVapidPublicKey(
          typeof data === "string" ? data : null
        );
        if (key) return key;
      } catch {
        // jatuh ke site_settings
      }
    }
    try {
      let q = supabase
        .from("site_settings")
        .select("value")
        .eq("key", SITE_SETTING_KEYS.vapidPublicKey);
      if (storeId) q = q.eq("store_id", storeId);
      const { data } = await q.maybeSingle();
      const key = normalizeVapidPublicKey((data as { value: string | null } | null)?.value);
      if (key) return key;
    } catch {
      // jatuh ke env build
    }
  }
  return normalizeVapidPublicKey(VAPID_PUBLIC_KEY);
}

/**
 * Toko pemilik SESI klien ini. Dibaca dari baris milik pengguna sendiri
 * (customers/profiles — keduanya RLS owner-scoped), bukan dari Host: kunci push
 * harus mengikuti toko AKUN, sama seperti seluruh data yang dilihatnya.
 * Null = tak diketahui → pemanggil jatuh ke jalur lama.
 */
async function resolveClientStoreId(supabase: NonNullable<ReturnType<typeof clientFor>>): Promise<string | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;
    const { data: cust } = await supabase
      .from("customers")
      .select("store_id")
      .eq("auth_user_id", user.id)
      .maybeSingle();
    const fromCustomer = (cust as { store_id?: string | null } | null)?.store_id ?? null;
    if (fromCustomer) return fromCustomer;
    const { data: prof } = await supabase
      .from("profiles")
      .select("store_id")
      .eq("id", user.id)
      .maybeSingle();
    return (prof as { store_id?: string | null } | null)?.store_id ?? null;
  } catch {
    return null;
  }
}

/** Minta izin, berlangganan PushManager, simpan baris DB untuk scope ini. */
export async function enablePush(
  scope: PushScope,
  customerId: string | null
): Promise<Result> {
  if (!isPushSupported()) {
    return { ok: false, error: "Perangkat/peramban ini belum mendukung notifikasi push." };
  }
  const vapidKey = await resolveVapidPublicKey(scope);
  if (!vapidKey) {
    return {
      ok: false,
      error:
        "Kunci VAPID belum dikonfigurasi. Admin: isi Kunci Publik VAPID di Pengaturan Website (atau set NEXT_PUBLIC_VAPID_PUBLIC_KEY saat build).",
    };
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, error: "Izin notifikasi belum diberikan." };
  }
  const reg = await readyRegistration();
  if (!reg) return { ok: false, error: "Service worker belum aktif." };

  const supabase = clientFor(scope);
  if (!supabase) return { ok: false, error: "Koneksi Supabase tidak tersedia." };

  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // Kunci berubah (rotasi/ganti pasangan kunci) → langganan lama tak valid
    // untuk kunci baru; lepas dulu supaya subscribe ulang memakai kunci baru.
    const same = sameApplicationServerKey(
      sub.options?.applicationServerKey ?? null,
      vapidKey
    );
    if (same === false) {
      try {
        await sub.unsubscribe();
      } catch {
        // biarkan — subscribe di bawah akan melempar bila memang bentrok
      }
      sub = null;
    }
  }
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });
    } catch (e) {
      return {
        ok: false,
        error:
          "Gagal berlangganan push: " +
          (e instanceof Error ? e.message : "layanan push tidak merespons."),
      };
    }
  }
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!p256dh || !auth) {
    return { ok: false, error: "Langganan push tidak lengkap." };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { ok: false, error: "Sesi tidak ditemukan, silakan login ulang." };

  const { error } = await supabase.from("push_subscriptions").upsert(
    {
      endpoint: sub.endpoint,
      p256dh,
      auth,
      scope,
      auth_user_id: user.id,
      customer_id: scope === "customer" ? customerId : null,
      user_agent: navigator.userAgent.slice(0, 300),
      failure_count: 0,
      disabled_at: null,
      // Cermin preferensi opt-out perangkat ini agar push tersaring di server.
      muted_types: mutedTypesForScope(scope),
    },
    { onConflict: "endpoint,auth_user_id" }
  );
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/**
 * Sinkronkan daftar jenis yang di-mute (dari prefs localStorage) ke baris
 * langganan perangkat ini, agar Edge Function tak mengirim push untuk jenis
 * yang di-opt-out. Best-effort & no-op bila push tak aktif di perangkat ini.
 */
export async function syncPushPrefs(
  scope: PushScope,
  prefs: Record<string, boolean>
): Promise<void> {
  if (scope !== "customer") return; // notif admin sudah disaring saat dibuat
  if (!isPushSupported() || Notification.permission !== "granted") return;
  const reg = await readyRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) return;
  const supabase = clientFor(scope);
  if (!supabase) return;
  try {
    await supabase
      .from("push_subscriptions")
      .update({ muted_types: mutedTypesFromPrefs(prefs) })
      .eq("scope", scope)
      .eq("endpoint", sub.endpoint);
  } catch {
    // abaikan — penyaringan tampilan klien tetap berlaku sebagai fallback
  }
}

/**
 * Lepas push PERANGKAT INI untuk scope ini saat keluar (bughunt E9-4).
 * Best-effort dan berbatas waktu, supaya keluar tak pernah tertahan.
 *
 * Beda dengan `disablePush` (tombol notifikasi): tanpa langganan browser,
 * `disablePush` menghapus SEMUA baris scope milik akun ini — termasuk HP lain.
 * Saat keluar, perangkat tanpa langganan memang tak punya apa pun untuk
 * dilepas, jadi hanya endpoint perangkat ini yang dihapus.
 *
 * Wajib dipanggil SEBELUM `signOut`: RLS hanya mengizinkan pemilik menghapus
 * barisnya. `getRegistration()` dipakai alih-alih `serviceWorker.ready`, yang
 * tak pernah selesai bila service worker belum terpasang.
 */
export async function detachPushOnThisDevice(scope: PushScope, timeoutMs = 3000): Promise<void> {
  const work = (async () => {
    if (!isPushSupported()) return;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (!sub) return;
    const supabase = clientFor(scope);
    if (!supabase) return;
    await supabase.from("push_subscriptions").delete().eq("scope", scope).eq("endpoint", sub.endpoint);
  })().catch(() => undefined);
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    work,
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, timeoutMs);
    })
  ]);
  if (timer) clearTimeout(timer);
}

/** Hapus baris DB untuk scope ini (berhenti menerima push). Langganan browser
 *  TIDAK dilepas agar scope/role lain di perangkat yang sama tetap berfungsi. */
export async function disablePush(scope: PushScope): Promise<Result> {
  const supabase = clientFor(scope);
  if (!supabase) return { ok: false, error: "Koneksi Supabase tidak tersedia." };
  const reg = await readyRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  let query = supabase.from("push_subscriptions").delete().eq("scope", scope);
  if (sub) query = query.eq("endpoint", sub.endpoint);
  const { error } = await query;
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}
